import { PaginationSearchBase } from '../PaginationSearchBase'
import { action, computed, observable } from 'mobx'
import { computedFn } from 'mobx-utils'
import { PROJECT_MAIN_STORE } from '../ProjectMainStore'
import { RootStore } from '../RootStore'
import {
  DraggableColLabels,
  statsTableInitialSearchProps,
  StatsTableSearchProps,
  StatsTableSettings,
  SelectedCorrespondingEntitySets,
  SelectedCorrespondingEntitiesMap,
  ColumnFilterSelections,
  StatsTableColType,
  StatsTableEntityObject, FilterOptionSettings
} from './StatsBaseTableStore.types'
import { getMainImageSrcForObj, PaginationProps } from '../../utilities'
import { PRODUCTS_MAIN_STORE } from '../ProductsMainStore'
import { DragDropContextProps } from 'react-beautiful-dnd'
import { SelectTreeViewProps } from '../../components/shared'
import { BrandDTO, ProductDTO, ProjectDTO } from '../../models'
import { BRANDS_MAIN_STORE } from '../BrandsMainStore'
import { TreeViewDropdownLineProps } from '../../components/shared/SelectTreeView/TreeViewDropdownLine'
import { STATISTICS_PAGE_STORE } from '../StatisticsPageStore'
import { hasMatchingChild } from './StatsBaseTableStore.utils'

export class StatsBaseTableStore<SortProps, SearchResult>
  extends PaginationSearchBase<SortProps, StatsTableSearchProps, SearchResult> {
  rootStore: RootStore

  constructor (rootStore: RootStore, paginationProps: PaginationProps<SortProps>) {
    super(
      paginationProps,
      statsTableInitialSearchProps,
      []
    )
    this.rootStore = rootStore
  }

  @observable isExpandedInteractionsColumn: boolean = false
  @observable isExpandedWatchTimeColumn: boolean = false

  @observable tableSettings: StatsTableSettings = {
    draggableColumnsCount: 3,
    mainEntityColLabel: DraggableColLabels.PROJECT,
    primaryCorrespondingEntityColLabel: DraggableColLabels.PRODUCTS,
    secondaryCorrespondingEntityColLabel: DraggableColLabels.STORE
  }

  @observable /* private */ selectedCorrespondingEntitiesMap: SelectedCorrespondingEntitiesMap = new Map()
  @observable /* private */ selectedMainEntities: Map<number, string> = new Map()

  /* private */ statsMapKeysByProject: Map<number, Set<string>> = new Map()
  /* private */ statsMapKeysByProduct: Map<number, Set<string>> = new Map()
  /* private */ statsMapKeysByBrand: Map<number, Set<string>> = new Map()

  @observable /* private */ columnFilterSelections: ColumnFilterSelections = {
    main: new Set(),
    mainFull: new Set(),
    primary: new Set(),
    primaryFull: new Set(),
    secondary: new Set(),
    secondaryFull: new Set()
  }

  @action /* private */ setSelectedMainEntities = (selectedMainEntities: Map<number, string>) => {
    this.selectedMainEntities = selectedMainEntities
  }

  @action init = () => {
    this.selectedMainEntities = new Map()
    this.selectedCorrespondingEntitiesMap = new Map()
    this.searchResults = []
  }

  @action setTableSetting = (settings: Partial<StatsTableSettings>) => {
    this.tableSettings = {
      ...this.tableSettings,
      ...settings
    }
  }

  @action setIsExpandedInteractionsColumn = (isExpandable: boolean) => {
    this.isExpandedInteractionsColumn = isExpandable;
  }

  @action setIsExpandedWatchTimeColumn = (isExpandable: boolean) => {
    this.isExpandedWatchTimeColumn = isExpandable;
  }

  @action /* private */ setColumnFilterSelections = (
    selections: Partial<ColumnFilterSelections>
  ): void => {
    // console.log('column filter selections', selections)
    this.columnFilterSelections = {
      ...this.columnFilterSelections,
      ...selections
    }
  }

  @action /* private */ onFilterEntityCheckboxClick = (
    colType: StatsTableColType,
    entityIdKey: number | string
  ): void => {
    if (this.isFilterEntitySelected(colType, entityIdKey)) {
      this.columnFilterSelections[colType].delete(entityIdKey as never)
    } else {
      this.columnFilterSelections[colType].add(entityIdKey as never)
    }
  }

  @action /* private */ onFilterAggregationLineCheckboxClick = (
    colType: StatsTableColType,
    entityIdKeys: number[] | string[],
    action: 'select' | 'deselect'
  ): void => {
    if (action === 'select') {
      entityIdKeys.forEach((idKey: number | string) => {
        this.columnFilterSelections[colType].add(idKey as never)
      })
    } else {
      entityIdKeys.forEach((idKey: number | string) => {
        this.columnFilterSelections[colType].delete(idKey as never)
      })
    }
  }

  @observable getEntityGraphColor = computedFn(
    (entityId: number): string => this.selectedMainEntities.get(entityId)
  )

  @observable isMainEntitySelected = computedFn(
    (entityId: number): boolean => {
      return this.selectedMainEntities.has(this.getParentId(entityId))
    }
  )

  @observable isSomeCorrespondingEntitySelected = computedFn(
    (
      mainEntityId: number,
      correspondingEntityIds: number[],
      columnType: StatsTableColType
    ): boolean => {
      return this.getSelectedCorrespondingEntitiesCount(
        mainEntityId,
        correspondingEntityIds,
        columnType
      ) > 0
    }
  )

  @observable getSelectedCorrespondingEntitiesCount = computedFn(
    (
      mainEntityId: number,
      correspondingEntityIds: number[],
      columnType: StatsTableColType
    ): number => {
      return correspondingEntityIds.reduce(
        (count, id) => {
          const isSelected = this.isCorrespondingEntitySelected(
            mainEntityId,
            id,
            columnType
          )
          return isSelected
            ? count + 1
            : count
        },
        0
      )
    }
  )

  @observable isCorrespondingEntitySelected = computedFn(
    (
      mainEntityId: number,
      correspondingEntityId: number,
      columnType: StatsTableColType
    ): boolean => {
      const columnLabel = this.getEntityColLabelByColType(columnType)
      const selectedCorrespondingEntitySets: SelectedCorrespondingEntitySets =
        this.getSelectedCorrespondingEntitySets(mainEntityId)
      if (!selectedCorrespondingEntitySets) return false

      const isCorrespondingEntityProject = columnLabel === DraggableColLabels.PROJECT
      if (
        isCorrespondingEntityProject &&
        this.rootStore[PROJECT_MAIN_STORE].doesProjectHasChildren(correspondingEntityId)
      ) {
        const childrenIds = [
          ...this.rootStore[PROJECT_MAIN_STORE].getProjectChildrenIds(correspondingEntityId)
        ]
        return childrenIds.some(id =>
          selectedCorrespondingEntitySets[columnLabel].has(id)
        )
      } else {
        return selectedCorrespondingEntitySets?.[columnLabel]?.has(correspondingEntityId)
      }
    }
  )

  @observable /* private */ isFilterEntitySelected = computedFn(
    (colType: StatsTableColType, entityIdKey: number | string): boolean => {
      const idKeysSet = this.columnFilterSelections[colType] as Set<number | string>
      return Boolean(idKeysSet?.has(entityIdKey))
    }
  )

  @observable getFilterPropsByColLabel = computedFn(
    (colType: StatsTableColType): SelectTreeViewProps => {
      const {
        mainEntityColLabel,
        primaryCorrespondingEntityColLabel
      } = this.tableSettings

      // console.log('table settings', this.tableSettings)
      const label = this.getEntityColLabelByColType(colType)

      let options: SelectTreeViewProps['options'] = []
      const entityIdKeysForMainEntity = new Set<number | string>()

      // let dropdownDisabled: boolean = false

      if (colType === StatsTableColType.MAIN) {
        // dropdownDisabled = this.columnFilterSelections.main.size < 2
        switch (label) {
          case DraggableColLabels.STORE: {
            options = this.tableBrands.map(brand => {
              entityIdKeysForMainEntity.add(brand.id)

              return {
                label: brand.name,
                isSelected: this.isFilterEntitySelected(colType, brand.id),
                onClick: () => this.onFilterEntityCheckboxClick(colType, brand.id)
              }
            })

            break
          }
          case DraggableColLabels.PROJECT: {
            const parseProjectsToOptions = (projects: ProjectDTO[]): SelectTreeViewProps['options'] => {
              if (!projects) return []

              return projects.map(project => {
                const hasChildren = Boolean(project.children && project.children.length)
                const children: SelectTreeViewProps['options'] = hasChildren
                  ? parseProjectsToOptions(project.children)
                  : null
                const isSelected = hasChildren
                  ? !children.some(childOption => !childOption.isSelected)
                  : this.columnFilterSelections[colType]?.has(project.id)
                const onClick = (): void => {
                  if (hasChildren) {
                    children.forEach(childOption =>
                      childOption.onClick && childOption.onClick()
                    )
                  } else {
                    this.onFilterEntityCheckboxClick(colType, project.id)
                  }
                }

                !hasChildren && entityIdKeysForMainEntity.add(project.id)

                return {
                  label: project.name,
                  isExpandable: hasChildren,
                  isSelected,
                  children,
                  onClick
                }
              })
            }

            options = parseProjectsToOptions(this.tableProjects)
            break
          }
          case DraggableColLabels.PRODUCTS: {
            options = this.tableProducts.map(product => {
              entityIdKeysForMainEntity.add(product.id)

              return {
                label: product.name,
                image: getMainImageSrcForObj(product),
                isSelected: this.isFilterEntitySelected(colType, product.id),
                onClick: () => this.onFilterEntityCheckboxClick(colType, product.id)
              }
            })
            break
          }
          default: break
        }
      }

      const {
        [PROJECT_MAIN_STORE]: {
          getProjectById
        },
        [PRODUCTS_MAIN_STORE]: {
          getProductById
        },
        [BRANDS_MAIN_STORE]: {
          getBrandById
        }
      } = this.rootStore

      let mainEntities: StatsTableEntityObject[] = []
      let getPrimaryEntityIds = (_: number) => new Set<number>()
      let getPrimaryEntityById = (_: number): StatsTableEntityObject => null

      const filterOptionSettings: FilterOptionSettings = {}

      if (colType === StatsTableColType.PRIMARY) {
        // dropdownDisabled = this.columnFilterSelections.main.size < 2
        //   ? false
        //   : this.columnFilterSelections.main.size ===
        //   this.columnFilterSelections.mainFull.size
        switch (mainEntityColLabel) {
          case DraggableColLabels.STORE: {
            mainEntities = this.tableBrands
            switch (primaryCorrespondingEntityColLabel) {
              case DraggableColLabels.PROJECT: {
                getPrimaryEntityIds = this.getProjectIdsForBrand
                getPrimaryEntityById = getProjectById
                break
              }
              case DraggableColLabels.PRODUCTS: {
                getPrimaryEntityIds = this.getProductIdsForBrand
                getPrimaryEntityById = getProductById
                filterOptionSettings.showImageForPrimary = true
                break
              }
              default: break
            }
            break
          }
          case DraggableColLabels.PROJECT: {
            mainEntities = this.tableProjects
            switch (primaryCorrespondingEntityColLabel) {
              case DraggableColLabels.STORE: {
                getPrimaryEntityIds = this.getBrandIdsForProject
                getPrimaryEntityById = getBrandById
                break
              }
              case DraggableColLabels.PRODUCTS: {
                getPrimaryEntityIds = this.getProductIdsForProject
                getPrimaryEntityById = getProductById
                filterOptionSettings.showImageForPrimary = true
                break
              }
              default: break
            }
            break
          }
          case DraggableColLabels.PRODUCTS: {
            mainEntities = this.tableProducts
            filterOptionSettings.showImageForMain = true
            switch (primaryCorrespondingEntityColLabel) {
              case DraggableColLabels.PROJECT: {
                getPrimaryEntityIds = this.getProjectIdsForProduct
                getPrimaryEntityById = getProjectById
                break
              }
              case DraggableColLabels.STORE: {
                mainEntities = []
                // this filter combination makes no sense so no filter options should be rendered
                // getPrimaryEntityIds = getBrandIdsForProduct
                // getPrimaryEntityById = getBrandById
                break
              }
              default: break
            }
            break
          }
          default: break
        }

        options = this.getFilterOptionsForPrimaryCorrespondingEntitiesColumn({
          entityIdKeysForMainEntity,
          getPrimaryEntityById,
          getPrimaryEntityIds,
          mainEntities,
          settings: filterOptionSettings
        })
      }

      if (colType === StatsTableColType.SECONDARY) {
        // dropdownDisabled = this.columnFilterSelections.primary.size < 2
        //   ? false
        //   : this.columnFilterSelections.primary.size ===
        //   this.columnFilterSelections.primaryFull.size
        // console.log({ dropdownDisabled })
        // console.log('this.columnFilterSelections.primary.size', this.columnFilterSelections.primary.size)
        let getSecondaryEntityIds = (_: number) => new Set<number>()
        let getSecondaryEntityById = (_: number): StatsTableEntityObject => null

        switch (mainEntityColLabel) {
          case DraggableColLabels.STORE: {
            mainEntities = this.tableBrands
            switch (primaryCorrespondingEntityColLabel) {
              case DraggableColLabels.PROJECT: {
                getPrimaryEntityIds = this.getProjectIdsForBrand
                getPrimaryEntityById = getProjectById
                getSecondaryEntityIds = this.getProductIdsForProject
                getSecondaryEntityById = getProductById
                filterOptionSettings.showImageForSecondary = true
                break
              }
              case DraggableColLabels.PRODUCTS: {
                getPrimaryEntityIds = this.getProductIdsForBrand
                getPrimaryEntityById = getProductById
                getSecondaryEntityIds = this.getProjectIdsForProduct
                getSecondaryEntityById = getProjectById
                filterOptionSettings.showImageForPrimary = true
                break
              }
              default: break
            }
            break
          }
          case DraggableColLabels.PROJECT: {
            mainEntities = this.tableProjects
            switch (primaryCorrespondingEntityColLabel) {
              case DraggableColLabels.STORE: {
                getPrimaryEntityIds = this.getBrandIdsForProject
                getPrimaryEntityById = getBrandById
                getSecondaryEntityIds = this.getProductIdsForBrand
                getSecondaryEntityById = getProductById
                filterOptionSettings.showImageForSecondary = true
                break
              }
              case DraggableColLabels.PRODUCTS: {
                mainEntities = []
                // getPrimaryEntityIds = getProductIdsForProject
                // getPrimaryEntityById = getProductById

                // // filtering secondaryCorrespondingCol makes no sense in this combination
                // getSecondaryEntityIds = getBrandIdsForProduct
                // getSecondaryEntityById = getBrandById

                filterOptionSettings.showImageForPrimary = true
                break
              }
              default: break
            }
            break
          }
          case DraggableColLabels.PRODUCTS: {
            mainEntities = this.tableProducts
            filterOptionSettings.showImageForMain = true
            switch (primaryCorrespondingEntityColLabel) {
              case DraggableColLabels.STORE: {
                // no-sense combination
                getPrimaryEntityIds = this.getBrandIdsForProduct
                getPrimaryEntityById = getBrandById
                getSecondaryEntityIds = this.getProjectIdsForProduct
                getSecondaryEntityById = getProjectById
                filterOptionSettings.skipPrimaryEntityGrouping = true
                break
              }
              case DraggableColLabels.PROJECT: {
                mainEntities = []
                // getPrimaryEntityIds = getProjectIdsForProduct
                // getPrimaryEntityById = getProjectById
                // getSecondaryEntityIds = getBrandIdsForProject
                // getSecondaryEntityById = getBrandById
                break
              }
              default: break
            }
            break
          }
          default: break
        }

        options = this.getFilterOptionsForSecondaryCorrespondingEntitiesColumn({
          entityIdKeysForMainEntity,
          getPrimaryEntityById,
          getPrimaryEntityIds,
          getSecondaryEntityById,
          getSecondaryEntityIds,
          mainEntities,
          settings: filterOptionSettings
        })
      }

      let optionsCount = 0
      let selectedOptionsCount = 0
      const feedOptionCounters = (opts: TreeViewDropdownLineProps[]) => {
        opts.forEach(option => {
          const hasChildren = Boolean(option.children && option.children.length)
          if (hasChildren) {
            feedOptionCounters(option.children)
          } else {
            optionsCount++
            option.isSelected && selectedOptionsCount++
          }
        })
      }
      feedOptionCounters(options)

      const areAllSelected = optionsCount === selectedOptionsCount
      const selectAllSecondaryLabelText = areAllSelected
        ? `(${optionsCount})`
        : `(${selectedOptionsCount}/${optionsCount})`
      const selectAllOptionProps: SelectTreeViewProps['selectAllOptionProps'] = {
        label: 'Select all',
        secondaryLabelText: selectAllSecondaryLabelText,
        isSelected: areAllSelected,
        onClick: () => this.onFilterAggregationLineCheckboxClick(
          colType,
          [...entityIdKeysForMainEntity as any],
          areAllSelected ? 'deselect' : 'select'
        )
      }

      return {
        // disabled: dropdownDisabled,
        selectAllOptionProps,
        options,
        onDropdownCollapse: () => {
          this.clearEntitySelectionsAndTotalPeriodData()
          this.triggerSearch({ resetPageNumber: true })
        }
      }
    }
  )

  @computed get legendEntityLabel (): string {
    switch (this.tableSettings.mainEntityColLabel) {
      case DraggableColLabels.PROJECT:
        return 'projects'
      case DraggableColLabels.PRODUCTS:
        return 'products'
      case DraggableColLabels.STORE:
        return 'stores'
      default:
        return 'entities'
    }
  }

  @computed get selectorColLabel (): string {
    return `${this.selectedMainEntities.size}/${Math.min(6, this.pagination.totalElements)}`
  }

  get isMainEntityStore (): boolean {
    return this.tableSettings.mainEntityColLabel === DraggableColLabels.STORE
  }

  get isMainEntityProject (): boolean {
    return this.tableSettings.mainEntityColLabel === DraggableColLabels.PROJECT
  }

  get isMainEntityProduct (): boolean {
    return this.tableSettings.mainEntityColLabel === DraggableColLabels.PRODUCTS
  }

  get tableProjects (): ProjectDTO[] {
    const idsSet = new Set(this.statsProjectIds)
    return Array.from(this.rootStore[PROJECT_MAIN_STORE].projectsHierarchyList
      .filter(project => hasMatchingChild(project, idsSet)))
  }

  getSelectedCorrespondingEntitySets = (
    mainEntityId: number
  ): SelectedCorrespondingEntitySets => {
    return this.selectedCorrespondingEntitiesMap.get(this.getParentId(mainEntityId))
  }

  /* private */ getParentId = (entityId: number): number => {
    if (!this.isMainEntityProject) return entityId

    return this.rootStore[PROJECT_MAIN_STORE]
      .getProjectParentId(entityId) || entityId
  }

  getBrandIdsForProduct = (productId: number): Set<number> => {
    const brandId = this.rootStore[PRODUCTS_MAIN_STORE].getProductBrandId(productId)
    return new Set([brandId])
  }

  getProductIdsForProject = (projectId: number): Set<number> => {
    return this.getIdsForEntity(projectId, 0, this.statsMapKeysByProject)
  }

  getProjectIdsForBrand = (brandId: number): Set<number> => {
    return this.getIdsForEntity(brandId, 1, this.statsMapKeysByBrand)
  }

  getProductIdsForBrand = (brandId: number): Set<number> => {
    return this.getIdsForEntity(brandId, 0, this.statsMapKeysByBrand)
  }

  getProjectIdsForProduct = (productId: number): Set<number> => {
    return this.getIdsForEntity(productId, 1, this.statsMapKeysByProduct)
  }

  private getIdsForEntity = (id: number, index: number, mapKeys: Map<number, Set<string>>): Set<number> => {
    const ids: Set<number> = new Set()
    const mapKeysForEntity = mapKeys.get(id) || []
    mapKeysForEntity.forEach(mapKey => {
      try {
        const entityId = Number(mapKey.split('-')[index])
        !Number.isNaN(entityId) && ids.add(entityId)
      } catch (error) {
        console.error(error)
      }
    })
    return ids
  }

  getBrandIdsForProject = (projectId: number): Set<number> => {
    const brandIds: Set<number> = new Set()
    const mapKeysForProject = this.statsMapKeysByProject.get(projectId) || []
    mapKeysForProject.forEach(mapKey => {
      try {
        const brandId = Number(mapKey.split('-').pop())
        !Number.isNaN(brandId) && brandIds.add(brandId)
      } catch (error) {
        console.error(error)
      }
    })
    return brandIds
  }

  onColumnDragEnd: DragDropContextProps['onDragEnd'] = (result) => {
    if (!result.destination) return

    const {
      mainEntityColLabel,
      primaryCorrespondingEntityColLabel,
      secondaryCorrespondingEntityColLabel
    } = this.tableSettings

    if (secondaryCorrespondingEntityColLabel) {
      if (result.destination.index < 1 || result.destination.index > 3) return
    } else {
      if (result.destination.index < 1 || result.destination.index > 2) return
    }

    const colLabels = [
      mainEntityColLabel,
      primaryCorrespondingEntityColLabel,
      secondaryCorrespondingEntityColLabel
    ]

    // the first column is the selector col which is not draggable
    // so we need to adopt the col index to the colLabels indexes
    const sourceIndex = result.source.index - 1
    const destinationIndex = result.destination.index - 1
    const colLabel = result.draggableId as DraggableColLabels

    const increment = destinationIndex < sourceIndex ? -1 : 1
    for (let k = sourceIndex; k !== destinationIndex; k += increment) {
      colLabels[k] = colLabels[k + increment]
    }
    colLabels[destinationIndex] = colLabel as DraggableColLabels

    this.setTableSetting({
      mainEntityColLabel: colLabels[0],
      primaryCorrespondingEntityColLabel: colLabels[1],
      secondaryCorrespondingEntityColLabel: colLabels[2]
    })

    this.refreshColumnFilterSelections()

    const isMainEntityColChanged = mainEntityColLabel !== colLabels[0]
    if (isMainEntityColChanged) {
      this.clearEntitySelectionsAndTotalPeriodData()
    }

    this.triggerSearch({ resetPageNumber: true })
  }

  clearEntitySelectionsAndTotalPeriodData = () => {
    // throw new Error('not implemented')
  }

  refreshColumnFilterSelections = (): void => {
    const {
      mainEntityColLabel,
      primaryCorrespondingEntityColLabel
    } = this.tableSettings

    // console.log('refreshing columns fitters', this.tableSettings)
    // console.log('map keys by product', this.statsMapKeysByProduct)
    // console.log('map keys by project', this.statsMapKeysByProject)

    let mainEntityIds: number[] = []
    const primaryEntityIdKeys: string[] = []
    const secondaryEntityIdKeys: string[] = []

    const feedEntityIdKeys = (args: {
      getPrimaryEntityIds: (_: number) => Set<number>
      getSecondaryEntityIds: (_: number) => Set<number>
      getSecondaryEntityIdByMainEntityId?: (_: number) => number
      options?: {
        skipPrimaryEntityFiltering?: boolean
        skipSecondaryEntityFiltering?: boolean
      }
    }) => {
      const {
        getPrimaryEntityIds,
        getSecondaryEntityIds,
        getSecondaryEntityIdByMainEntityId =
        this.rootStore[PRODUCTS_MAIN_STORE].getProductBrandId,
        options
      } = args

      mainEntityIds.forEach(mainEntityId => {
        getPrimaryEntityIds(mainEntityId).forEach(primaryEntityId => {
          primaryEntityIdKeys.push(`${mainEntityId}-${primaryEntityId}`)
          if (options?.skipSecondaryEntityFiltering) {
            secondaryEntityIdKeys
              .push(`${mainEntityId}-${primaryEntityId
                }-${getSecondaryEntityIdByMainEntityId(mainEntityId)}`)
          } else {
            getSecondaryEntityIds(
              options?.skipPrimaryEntityFiltering
                ? mainEntityId
                : primaryEntityId
            ).forEach(secondaryEntityId => {
              secondaryEntityIdKeys
                .push(`${mainEntityId}-${primaryEntityId}-${secondaryEntityId}`)
            })
          }
        })
      })
    }

    switch (mainEntityColLabel) {
      case DraggableColLabels.STORE: {
        mainEntityIds = this.statsBrandIds

        switch (primaryCorrespondingEntityColLabel) {
          case DraggableColLabels.PROJECT: {
            feedEntityIdKeys({
              getPrimaryEntityIds: this.getProjectIdsForBrand,
              getSecondaryEntityIds: this.getProductIdsForProject
            })
            break
          }
          case DraggableColLabels.PRODUCTS: {
            feedEntityIdKeys({
              getPrimaryEntityIds: this.getProductIdsForBrand,
              getSecondaryEntityIds: this.getProjectIdsForProduct
            })
            break
          }
          default: break
        }
        break
      }
      case DraggableColLabels.PROJECT: {
        mainEntityIds = this.statsProjectIds

        switch (primaryCorrespondingEntityColLabel) {
          case DraggableColLabels.STORE: {
            feedEntityIdKeys({
              getPrimaryEntityIds: this.getBrandIdsForProject,
              getSecondaryEntityIds: this.getProductIdsForBrand
            })
            break
          }
          case DraggableColLabels.PRODUCTS: {
            feedEntityIdKeys({
              getPrimaryEntityIds: this.getProductIdsForProject,
              getSecondaryEntityIds: this.getBrandIdsForProduct
            })
            break
          }
          default: break
        }
        break
      }
      case DraggableColLabels.PRODUCTS: {
        mainEntityIds = this.statsProductIds

        switch (primaryCorrespondingEntityColLabel) {
          case DraggableColLabels.STORE: {
            feedEntityIdKeys({
              getPrimaryEntityIds: this.getBrandIdsForProduct,
              getSecondaryEntityIds: this.getProjectIdsForProduct,
              options: {
                skipPrimaryEntityFiltering: true
              }
            })
            break
          }
          case DraggableColLabels.PROJECT: {
            feedEntityIdKeys({
              getPrimaryEntityIds: this.getProjectIdsForProduct,
              getSecondaryEntityIds: this.getProductIdsForProject,
              options: {
                skipSecondaryEntityFiltering: true
              }
            })
            break
          }
          default: break
        }
        break
      }
      default: break
    }

    this.setColumnFilterSelections({
      main: new Set(mainEntityIds),
      mainFull: new Set(mainEntityIds),
      primary: new Set(primaryEntityIdKeys),
      primaryFull: new Set(primaryEntityIdKeys),
      secondary: new Set(secondaryEntityIdKeys),
      secondaryFull: new Set(secondaryEntityIdKeys)
    })
  }

  /* private */ getEntityColLabelByColType = (colType: StatsTableColType)
    : DraggableColLabels => {
    const {
      mainEntityColLabel,
      primaryCorrespondingEntityColLabel,
      secondaryCorrespondingEntityColLabel
    } = this.tableSettings

    switch (colType) {
      case StatsTableColType.MAIN:
        return mainEntityColLabel
      case StatsTableColType.PRIMARY:
        return primaryCorrespondingEntityColLabel
      case StatsTableColType.SECONDARY:
        return secondaryCorrespondingEntityColLabel
      default:
        return null
    }
  }

  get tableBrands (): BrandDTO[] {
    const {
      [STATISTICS_PAGE_STORE]: {
        selectedStoreIds
      }
    } = this.rootStore

    const brandIds = selectedStoreIds.length
      ? this.statsBrandIds.filter(id => selectedStoreIds.includes(id))
      : this.statsBrandIds
    return this.getEntities('brandId', brandIds) as BrandDTO[]
  }

  get statsProjectIds (): number[] {
    return Array.from(this.statsMapKeysByProject.keys())
  }

  get statsBrandIds (): number[] {
    return Array.from(this.statsMapKeysByBrand.keys())
  }

  get statsProductIds (): number[] {
    return Array.from(this.statsMapKeysByProduct.keys())
  }

  /* private */ get tableProducts (): ProductDTO[] {
    return this.getEntities('productId', this.statsProductIds) as ProductDTO[]
  }

  /* private */ getEntities = (
    entityIdType: 'projectId' | 'productId' | 'brandId' | string,
    ids: number[]
  ): ProjectDTO[] | ProductDTO[] | BrandDTO[] => {
    const {
      [PROJECT_MAIN_STORE]: {
        getProjectById
      },
      [BRANDS_MAIN_STORE]: {
        getBrandById
      },
      [PRODUCTS_MAIN_STORE]: {
        getProductById
      }
    } = this.rootStore

    switch (entityIdType) {
      case 'projectId':
        return ids.map(getProjectById)
      case 'productId':
        return ids.map(getProductById)
      case 'brandId':
        return ids.map(getBrandById)
      default:
        return []
    }
  }

  /* private */ getFilterOptionsForPrimaryCorrespondingEntitiesColumn = (args: {
    mainEntities: StatsTableEntityObject[]
    getPrimaryEntityIds: (_: number) => Set<number>
    getPrimaryEntityById: (_: number) => StatsTableEntityObject
    entityIdKeysForMainEntity: Set<number | string>
    settings?: FilterOptionSettings
  }): TreeViewDropdownLineProps[] => {
    const {
      mainEntities,
      getPrimaryEntityIds,
      getPrimaryEntityById,
      entityIdKeysForMainEntity,
      settings
    } = args

    const colType = StatsTableColType.PRIMARY

    return mainEntities
      .filter(({ id }) => this.columnFilterSelections.main.has(id))
      .map(mainEntity => {
        const primaryEntityIds = getPrimaryEntityIds(mainEntity.id)
        const entityIdKeysForPrimaryEntity = new Set<string>()

        const children: TreeViewDropdownLineProps[] = Array
          .from(primaryEntityIds.values())
          .map(primaryEntityId => {
            const entityIdKey = `${mainEntity.id}-${primaryEntityId}`

            entityIdKeysForPrimaryEntity.add(entityIdKey)
            entityIdKeysForMainEntity.add(entityIdKey)

            const primaryEntity = getPrimaryEntityById(primaryEntityId)
            return {
              label: primaryEntity?.name,
              image: settings?.showImageForPrimary && getMainImageSrcForObj(primaryEntity),
              isSelected: this.isFilterEntitySelected(colType, entityIdKey),
              onClick: () => this.onFilterEntityCheckboxClick(colType, entityIdKey)
            }
          })

        const isSelected = children.some(childLine => childLine.isSelected)
        return {
          label: mainEntity.name,
          image: settings?.showImageForMain && getMainImageSrcForObj(mainEntity),
          isSelected,
          onClick: () => this.onFilterAggregationLineCheckboxClick(
            colType,
            [...entityIdKeysForPrimaryEntity],
            isSelected ? 'deselect' : 'select'
          ),
          children
        }
      })
  }

  /* private */ getFilterOptionsForSecondaryCorrespondingEntitiesColumn = (args: {
    mainEntities: StatsTableEntityObject[]
    getPrimaryEntityIds: (_: number) => Set<number>
    getPrimaryEntityIdByMainEntityId?: (_: number) => number
    getPrimaryEntityById: (_: number) => StatsTableEntityObject
    getSecondaryEntityIds: (_: number) => Set<number>
    getSecondaryEntityById: (_: number) => StatsTableEntityObject
    entityIdKeysForMainEntity: Set<number | string>
    settings?: FilterOptionSettings
  }): TreeViewDropdownLineProps[] => {
    const {
      mainEntities,
      getPrimaryEntityIds,
      getPrimaryEntityIdByMainEntityId =
      this.rootStore[PRODUCTS_MAIN_STORE].getProductBrandId,
      getPrimaryEntityById,
      getSecondaryEntityIds,
      getSecondaryEntityById,
      entityIdKeysForMainEntity,
      settings
    } = args

    const colType = StatsTableColType.SECONDARY

    return mainEntities
      .filter(({ id }) => this.columnFilterSelections.main.has(id))
      .map(mainEntity => {
        let children: TreeViewDropdownLineProps[] = []
        const entityIdKeysForPrimaryEntity = new Set<string>()

        if (settings.skipPrimaryEntityGrouping) {
          const secondaryEntityIds = getSecondaryEntityIds(mainEntity.id)
          const primaryEntityId = getPrimaryEntityIdByMainEntityId(mainEntity.id)

          children = Array.from(secondaryEntityIds)
            .map(secondaryEntityId => {
              const entityIdKey =
                `${mainEntity.id}-${primaryEntityId}-${secondaryEntityId}`

              entityIdKeysForPrimaryEntity.add(entityIdKey)
              entityIdKeysForMainEntity.add(entityIdKey)

              const secondaryEntity = getSecondaryEntityById(secondaryEntityId)

              return {
                label: secondaryEntity?.name,
                image: settings?.showImageForSecondary &&
                  getMainImageSrcForObj(secondaryEntity),
                isSelected: this.isFilterEntitySelected(colType, entityIdKey),
                onClick: () => this.onFilterEntityCheckboxClick(
                  colType,
                  entityIdKey
                )
              }
            })
        } else {
          children = Array.from(getPrimaryEntityIds(mainEntity.id).values())
            .filter(id => this.columnFilterSelections.primary.has(`${mainEntity.id}-${id}`))
            .map(primaryEntityId => {
              const entityIdKeysForSecondaryEntity = new Set<string>()
              const subChildren: TreeViewDropdownLineProps[] = Array
                .from(getSecondaryEntityIds(primaryEntityId))
                .map(secondaryEntityId => {
                  const entityIdKey =
                    `${mainEntity.id}-${primaryEntityId}-${secondaryEntityId}`

                  entityIdKeysForSecondaryEntity.add(entityIdKey)
                  entityIdKeysForPrimaryEntity.add(entityIdKey)
                  entityIdKeysForMainEntity.add(entityIdKey)

                  const secondaryEntity = getSecondaryEntityById(secondaryEntityId)
                  return {
                    label: secondaryEntity?.name,
                    image: settings?.showImageForSecondary &&
                      getMainImageSrcForObj(secondaryEntity),
                    isSelected: this.isFilterEntitySelected(colType, entityIdKey),
                    onClick: () => this.onFilterEntityCheckboxClick(
                      colType,
                      entityIdKey
                    )
                  }
                })

              const isChildSelected = subChildren.some(subChild => subChild.isSelected)
              const primaryEntity = getPrimaryEntityById(primaryEntityId)
              return {
                label: primaryEntity?.name,
                image: settings?.showImageForPrimary && getMainImageSrcForObj(primaryEntity),
                isSelected: isChildSelected,
                onClick: () => this.onFilterAggregationLineCheckboxClick(
                  colType,
                  [...entityIdKeysForSecondaryEntity],
                  isChildSelected ? 'deselect' : 'select'
                ),
                children: subChildren
              }
            })
        }

        const isSelected = children.some(childLine => childLine.isSelected)
        return {
          label: mainEntity?.name,
          image: settings?.showImageForMain && getMainImageSrcForObj(mainEntity),
          isSelected,
          onClick: () => this.onFilterAggregationLineCheckboxClick(
            colType,
            [...entityIdKeysForPrimaryEntity],
            isSelected ? 'deselect' : 'select'
          ),
          children
        }
      })
  }
}
